import apiClient from '@/components/plugins/axios';
import router from '@/router';

/**
 * Base64 解码 JWT token payload
 * @param {string} str - Base64 编码的字符串
 * @returns {object} 解码后的 JSON 对象
 */
function base64Decode(str) {
  // 处理 URL-safe base64 字符
  const base64Data = str.replace(/-/g, '+').replace(/_/g, '/');
  
  // 补齐 base64 字符串的 padding
  let padding = '';
  if (base64Data.length % 4 === 2) {
    padding = '==';
  } else if (base64Data.length % 4 === 3) {
    padding = '=';
  }
  
  // 使用 atob 解码 base64
  const decodedString = atob(base64Data + padding);
  
  // 处理 Unicode 字符
  const decodedBytes = new Uint8Array(decodedString.length);
  for (let i = 0; i < decodedString.length; i++) {
    decodedBytes[i] = decodedString.charCodeAt(i);
  }
  
  // 转换为 JSON
  try {
    return JSON.parse(new TextDecoder().decode(decodedBytes));
  } catch (e) {
    // 如果 TextDecoder 不可用，使用 fallback 方法
    return JSON.parse(decodeURIComponent(escape(decodedString)));
  }
}

/**
 * 解析 JWT token 获取 payload
 * @param {string} token - JWT token
 * @returns {object|null} token 的 payload，解析失败返回 null
 */
function parseToken(token) {
  try {
    if (!token) return null;
    
    // 移除可能的 "Bearer " 前缀和 token_type 前缀
    const cleanToken = token.replace(/^Bearer\s+/i, '').trim();
    const parts = cleanToken.split(' ');
    const actualToken = parts.length > 1 ? parts[parts.length - 1] : parts[0];
    
    const arr = actualToken.split('.');
    if (arr.length !== 3) return null;
    
    const payload = base64Decode(arr[1]);
    return payload;
  } catch (e) {
    console.error('Token 解析失败:', e);
    return null;
  }
}

/**
 * 检查 token 是否过期
 * @param {string} token - JWT token
 * @returns {boolean} true 表示过期，false 表示未过期
 */
function isTokenExpired(token) {
  const payload = parseToken(token);
  if (!payload || !payload.exp) return true;
  
  const exp = payload.exp * 1000; // 转换为毫秒
  const now = Date.now();
  
  return exp < now;
}

/**
 * 刷新 access_token
 * @returns {Promise<boolean>} 刷新成功返回 true，失败返回 false
 */
async function refreshAccessToken() {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return false;
    }
    
    // 调用刷新 token 接口
    // 根据小程序代码，接口可能期望接收字符串格式的 refresh_token
    const res = await apiClient.post('/api/v1/auth/token/refresh', refreshToken);
    
    if (res.data.code === 200) {
      const { data } = res.data;
      // 保存新的 token
      localStorage.setItem('access_token', `${data.token_type} ${data.access_token}`);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error('刷新 token 失败:', error);
    return false;
  }
}

/**
 * 清除所有认证相关的本地存储
 */
function clearAuthStorage() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('userInfo');
}

/**
 * 检查并处理 token 过期
 * 在应用启动时调用
 */
export async function checkAndRefreshToken() {
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');

  try {
    // 解析 access_token
    const accessPayload = parseToken(accessToken);
    if (!accessPayload || !accessPayload.exp) {
      // token 格式错误，清除并跳转登录
      clearAuthStorage();
      return;
    }
    
    // 解析 refresh_token
    const refreshPayload = refreshToken ? parseToken(refreshToken) : null;
    if (!refreshPayload || !refreshPayload.exp) {
      // refresh_token 格式错误，清除并跳转登录
      clearAuthStorage();
      return;
    }
    
    // 检查 access_token 是否过期
    const accessExp = accessPayload.exp * 1000;
    const refreshExp = refreshPayload.exp * 1000;
    const now = Date.now();
    
    if (accessExp < now) {
      // access_token 已过期
      if (refreshExp < now) {
        // refresh_token 也过期了，清除缓存并跳转登录
        clearAuthStorage();
      } else {
        // refresh_token 未过期，尝试刷新 access_token
        const refreshSuccess = await refreshAccessToken();
        if (!refreshSuccess) {
          // 刷新失败，清除缓存并跳转登录
          clearAuthStorage();
        }
        // 刷新成功，继续正常流程
      }
    }
  } catch (e) {
    // 发生异常，清除缓存并跳转登录
    clearAuthStorage();
  }
}

export { parseToken, isTokenExpired, refreshAccessToken, clearAuthStorage };

